// Market Data Service for real-time Brazilian financial data
export interface MarketData {
  selic: {
    value: number;
    change: number;
    lastUpdate: string;
  };
  ipca: {
    value: number;
    change: number;
    lastUpdate: string;
  };
  ibovespa: {
    value: number;
    change: number;
    lastUpdate: string;
  };
  usdBrl: {
    value: number;
    change: number;
    lastUpdate: string;
  };
}

export interface ApiResponse {
  success: boolean;
  data?: MarketData;
  error?: string;
}

class MarketDataService {
  private apiKey: string;
  private baseUrl: string;
  private updateInterval: number = 60000; // 1 minute
  private listeners: ((data: MarketData) => void)[] = [];

  constructor() {
    // You'll need to get an API key from Alpha Vantage
    this.apiKey = process.env.REACT_APP_ALPHA_VANTAGE_API_KEY || 'DZI0BL4A4JGNYPJA';
    this.baseUrl = 'https://www.alphavantage.co/query';
  }

  // Add listener for real-time updates
  addListener(callback: (data: MarketData) => void) {
    this.listeners.push(callback);
  }

  // Remove listener
  removeListener(callback: (data: MarketData) => void) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  // Notify all listeners
  private notifyListeners(data: MarketData) {
    this.listeners.forEach(listener => listener(data));
  }

  // Fetch Ibovespa data from Alpha Vantage
  private async fetchIbovespa(): Promise<{ value: number; change: number }> {
    try {
      const response = await fetch(
        `${this.baseUrl}?function=GLOBAL_QUOTE&symbol=BVMF.IBOV&apikey=${this.apiKey}`
      );
      const data = await response.json();
      
      // Check for API errors first
      if (data['Error Message']) {
        console.error('Alpha Vantage API Error:', data['Error Message']);
        throw new Error(data['Error Message']);
      }
      
      if (data['Note']) {
        console.error('Alpha Vantage API Note:', data['Note']);
        throw new Error('API call frequency limit reached');
      }
      
      if (data['Global Quote']) {
        const quote = data['Global Quote'];
        const currentPrice = parseFloat(quote['05. price']) || 0;
        const previousClose = parseFloat(quote['08. previous close']) || 0;
        const change = currentPrice - previousClose;
        
        return {
          value: currentPrice,
          change: change
        };
      }
      
      // Log the actual response for debugging
      console.log('Ibovespa API Response:', data);
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Error fetching Ibovespa data:', error);
      // Return fallback data
      return { value: 127500, change: 0 };
    }
  }

  // Fetch USD/BRL exchange rate
  private async fetchUsdBrl(): Promise<{ value: number; change: number }> {
    try {
      // Try Alpha Vantage first
      const response = await fetch(
        `${this.baseUrl}?function=FX_DAILY&from_symbol=USD&to_symbol=BRL&apikey=${this.apiKey}`
      );
      const data = await response.json();
      
      // Check for API errors first
      if (data['Error Message']) {
        console.error('Alpha Vantage API Error:', data['Error Message']);
        throw new Error(data['Error Message']);
      }
      
      if (data['Note']) {
        console.error('Alpha Vantage API Note:', data['Note']);
        throw new Error('API call frequency limit reached');
      }
      
      if (data['Time Series (FX)']) {
        const timeSeries = data['Time Series (FX)'];
        const dates = Object.keys(timeSeries).sort();
        const latest = timeSeries[dates[0]];
        const previous = timeSeries[dates[1]];
        
        const currentPrice = parseFloat(latest['4. close']) || 0;
        const previousPrice = parseFloat(previous['4. close']) || 0;
        const change = currentPrice - previousPrice;
        
        return {
          value: currentPrice,
          change: change
        };
      }
      
      // Log the actual response for debugging
      console.log('USD/BRL API Response:', data);
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Error fetching USD/BRL data from Alpha Vantage:', error);
      
      // Try alternative source - BCB API for exchange rate
      try {
        const response = await fetch(
          'https://api.bcb.gov.br/dados/serie/bcdata.sgs.1/dados/ultimos/2?formato=json'
        );
        const data = await response.json();
        
        if (data && data.length >= 2) {
          const current = parseFloat(data[0].valor) || 0;
          const previous = parseFloat(data[1].valor) || 0;
          const change = current - previous;
          
          return {
            value: current,
            change: change
          };
        }
      } catch (altError) {
        console.error('Error fetching USD/BRL from BCB:', altError);
      }
      
      // Return fallback data
      return { value: 4.95, change: 0 };
    }
  }

  // Fetch Selic rate from BCB API (Banco Central do Brasil)
  private async fetchSelic(): Promise<{ value: number; change: number }> {
    try {
      // BCB API for Selic rate
      const response = await fetch(
        'https://api.bcb.gov.br/dados/serie/bcdata.sgs.432/dados/ultimos/2?formato=json'
      );
      const data = await response.json();
      
      if (data && data.length >= 2) {
        const current = parseFloat(data[0].valor) || 0;
        const previous = parseFloat(data[1].valor) || 0;
        const change = current - previous;
        
        return {
          value: current,
          change: change
        };
      }
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Error fetching Selic data:', error);
      // Return fallback data
      return { value: 11.75, change: 0 };
    }
  }

  // Fetch IPCA data from BCB API
  private async fetchIpca(): Promise<{ value: number; change: number }> {
    try {
      // BCB API for IPCA
      const response = await fetch(
        'https://api.bcb.gov.br/dados/serie/bcdata.sgs.433/dados/ultimos/2?formato=json'
      );
      const data = await response.json();
      
      if (data && data.length >= 2) {
        const current = parseFloat(data[0].valor) || 0;
        const previous = parseFloat(data[1].valor) || 0;
        const change = current - previous;
        
        return {
          value: current,
          change: change
        };
      }
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Error fetching IPCA data:', error);
      // Return fallback data
      return { value: 4.62, change: 0 };
    }
  }

  // Fetch all market data
  async fetchMarketData(): Promise<ApiResponse> {
    try {
      // Fetch BCB data first (free, no rate limits)
      const [selic, ipca] = await Promise.all([
        this.fetchSelic(),
        this.fetchIpca()
      ]);

      // Add delay to avoid Alpha Vantage rate limits
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Fetch Alpha Vantage data with delay between calls
      const ibovespa = await this.fetchIbovespa();
      await new Promise(resolve => setTimeout(resolve, 1000));
      const usdBrl = await this.fetchUsdBrl();

      const marketData: MarketData = {
        selic: {
          ...selic,
          lastUpdate: new Date().toISOString()
        },
        ipca: {
          ...ipca,
          lastUpdate: new Date().toISOString()
        },
        ibovespa: {
          ...ibovespa,
          lastUpdate: new Date().toISOString()
        },
        usdBrl: {
          ...usdBrl,
          lastUpdate: new Date().toISOString()
        }
      };

      this.notifyListeners(marketData);
      
      return {
        success: true,
        data: marketData
      };
    } catch (error) {
      console.error('Error fetching market data:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Start real-time updates
  startRealTimeUpdates() {
    // Initial fetch
    this.fetchMarketData();
    
    // Set up interval for updates
    setInterval(() => {
      this.fetchMarketData();
    }, this.updateInterval);
  }

  // Stop real-time updates
  stopRealTimeUpdates() {
    // Clear interval if needed
    // Note: In a real implementation, you'd store the interval ID
  }

  // Test function to debug API responses
  async testApiConnection(): Promise<void> {
    console.log('Testing API connections...');
    
    try {
      // Test Alpha Vantage connection
      const response = await fetch(
        `${this.baseUrl}?function=GLOBAL_QUOTE&symbol=BVMF.IBOV&apikey=${this.apiKey}`
      );
      const data = await response.json();
      console.log('Alpha Vantage Response:', data);
    } catch (error) {
      console.error('Alpha Vantage Test Error:', error);
    }

    try {
      // Test BCB connection
      const response = await fetch(
        'https://api.bcb.gov.br/dados/serie/bcdata.sgs.432/dados/ultimos/1?formato=json'
      );
      const data = await response.json();
      console.log('BCB Response:', data);
    } catch (error) {
      console.error('BCB Test Error:', error);
    }
  }
}

// Export singleton instance
export const marketDataService = new MarketDataService();
